export const getFetchAppFileEndpoint = ({
  caseId,
  fileId,
}: {
  caseId: string;
  fileId: string;
}) => `cases/${caseId}/attachments/${fileId}`;

export const getFetchAppFilesEndpoint = ({ caseId }: { caseId: string }) =>
  `cases/${caseId}/attachments`;

export const getUploadAppFileEndpoint = getFetchAppFilesEndpoint;

/**
 * How long to keep re-reading the attachment list after a signature.
 *
 * The archived document does not exist when the embed reports a signature. The
 *   backend measured the gap in production on both HelloSign documents it had:
 *   **16.5 s and 14.7 s** from the patient's click to the `case_files3` row.
 *   Most of that is the provider — their own callback-to-row segment is ~4 s.
 *
 * So a single refetch at the moment of signing, which is what this app did,
 *   missed the row every time rather than occasionally. `POLL_MAX_ATTEMPTS`
 *   covers 30 s, roughly double the measured worst case.
 *
 * Not finding the row is a normal intermediate state here, not an absence: the
 *   caller must not treat a poll that has not resolved yet as "no document".
 */
export const CASE_FILES_POLL_INTERVAL_MS = 2500;
export const CASE_FILES_POLL_MAX_ATTEMPTS = 12;

/**
 * Hard stop, however slow the reads are.
 *
 * The attempts are spaced by the interval *plus* however long each read takes,
 *   so the run stretches on a slow connection rather than dropping attempts.
 *   That is the right trade — an attempt that happens late still answers, and
 *   one that was cancelled answers never — but it means the nominal 30 s is a
 *   floor and not a bound. This is the bound, and it also catches the case the
 *   attempt counter cannot: a read that never resolves at all, which would
 *   otherwise leave the timer running with every tick skipped.
 */
export const CASE_FILES_POLL_DEADLINE_MS = 90_000;
